import { z } from 'zod'
import { zodEnumValues } from '@/utils/zod.js'
import { WeekDay, type WeekDayType } from '@/utils/date/getter.js'

// -- SCHEDULE (Frequency) --
export type ScheduleReportFrequencyType =
  | 'ONCE'
  | 'DAILY'
  | 'WEEKLY'
  | 'EVERY_TWO_WEEKS'
  | 'MONTHLY'
  | 'QUARTERLY'
  | 'WEEKDAYS'
  | 'YEARLY'
  | 'CUSTOM'
export const ScheduleReportFrequency = {
  Once: 'ONCE',
  Daily: 'DAILY',
  Weekly: 'WEEKLY',
  EveryTwoWeeks: 'EVERY_TWO_WEEKS',
  Monthly: 'MONTHLY',
  Quarterly: 'QUARTERLY',
  Weekdays: 'WEEKDAYS',
  Yearly: 'YEARLY',
  Custom: 'CUSTOM',
} as const satisfies Record<string, ScheduleReportFrequencyType>

// -- CUSTOM FREQUENCY --
export type ScheduleReportCustomFrequencyUnitType =
  | 'DAY'
  | 'WEEK'
  | 'MONTH'
  | 'YEAR'
export const ScheduleReportCustomFrequencyUnit = {
  Day: 'DAY',
  Week: 'WEEK',
  Month: 'MONTH',
  Year: 'YEAR',
} as const satisfies Record<string, ScheduleReportCustomFrequencyUnitType>

export type ScheduleReportCustomFrequencyEndsType =
  | 'NEVER'
  | 'AFTER_DATE'
  | 'AFTER_COUNT'
export const ScheduleReportCustomFrequencyEnds = {
  Never: 'NEVER',
  AfterDate: 'AFTER_DATE',
  AfterCount: 'AFTER_COUNT',
} as const satisfies Record<string, ScheduleReportCustomFrequencyEndsType>

export type ScheduleReportMonthlyOptionType =
  | 'FIRST_DAY'
  | 'LAST_DAY'
  | 'FIRST_MONDAY'
  | 'SPECIFIC_DAY'
export const ScheduleReportMonthlyOption = {
  FirstDay: 'FIRST_DAY',
  LastDay: 'LAST_DAY',
  FirstMonday: 'FIRST_MONDAY',
  SpecificDay: 'SPECIFIC_DAY',
} as const satisfies Record<string, ScheduleReportMonthlyOptionType>
export type ScheduleReportCustomFrequency = {
  repeatEvery: number
  repeatEveryUnit: ScheduleReportCustomFrequencyUnitType
  repeatOnDays: WeekDayType[] | null
  monthlyOption: ScheduleReportMonthlyOptionType | null
  endsType: ScheduleReportCustomFrequencyEndsType
  endsDate: string | null
  endsAfterOccurrences: number | null
}

// Shared schema used by both the parent form (useScheduleReportForm) and the modal (CustomFrequencyModal).
// Co-located with the type to keep the shape and its validation in sync.
export const customFrequencyZodSchema = z.object({
  repeatEvery: z.number().int().min(1),
  repeatEveryUnit: z.enum(zodEnumValues(ScheduleReportCustomFrequencyUnit)),
  repeatOnDays: z.array(z.enum(zodEnumValues(WeekDay))).nullable(),
  monthlyOption: z.enum(zodEnumValues(ScheduleReportMonthlyOption)).nullable(),
  endsType: z.enum(zodEnumValues(ScheduleReportCustomFrequencyEnds)),
  endsDate: z.string().nullable(),
  endsAfterOccurrences: z.number().int().nullable(),
})

// -- FREQUENCY CONFIG MAPPING --
export type ScheduleReportFrequencyConfig = {
  freq_type: ScheduleReportFrequencyType
  interval?: number
  unit?: ScheduleReportCustomFrequencyUnitType
  weekdays?: WeekDayType[]
  monthly_option?: ScheduleReportMonthlyOptionType
  monthly_day?: number
  ends?: {
    ends_type: ScheduleReportCustomFrequencyEndsType
    count?: number
    date?: string
  } | null
}

// -- EXPORT FORMAT --
export type ScheduleReportExportFormatType = 'CSV' | 'XLSX' | 'PDF'
export const ScheduleReportExportFormat = {
  Csv: 'CSV',
  Xlsx: 'XLSX',
  Pdf: 'PDF',
} as const satisfies Record<string, ScheduleReportExportFormatType>

// -- DATA COLLECTION PERIOD (Report Period) --
export type ScheduleReportDataCollectionPeriodType =
  | 'CURRENT_DAY'
  | 'PREVIOUS_DAY'
  | 'LAST_7_DAYS'
  | 'LAST_30_DAYS'
  | 'LAST_90_DAYS'
  | 'LAST_365_DAYS'
  | 'LAST_WEEK'
  | 'LAST_MONTH'
  | 'LAST_QUARTER'
  | 'LAST_YEAR'
  | 'WEEK_TO_DATE'
  | 'MONTH_TO_DATE'
  | 'QUARTER_TO_DATE'
  | 'YEAR_TO_DATE'
  | 'LAST_QUARTER_MINUS_1'
  | 'LAST_QUARTER_MINUS_2'
  | 'LAST_QUARTER_MINUS_3'
  | 'LAST_QUARTER_MINUS_4'
export const ScheduleReportDataCollectionPeriod = {
  CurrentDay: 'CURRENT_DAY',
  PreviousDay: 'PREVIOUS_DAY',
  Last7Days: 'LAST_7_DAYS',
  Last30Days: 'LAST_30_DAYS',
  Last90Days: 'LAST_90_DAYS',
  Last365Days: 'LAST_365_DAYS',
  LastWeek: 'LAST_WEEK',
  LastMonth: 'LAST_MONTH',
  LastQuarter: 'LAST_QUARTER',
  LastYear: 'LAST_YEAR',
  WeekToDate: 'WEEK_TO_DATE',
  MonthToDate: 'MONTH_TO_DATE',
  QuarterToDate: 'QUARTER_TO_DATE',
  YearToDate: 'YEAR_TO_DATE',
  LastQuarterMinus1: 'LAST_QUARTER_MINUS_1',
  LastQuarterMinus2: 'LAST_QUARTER_MINUS_2',
  LastQuarterMinus3: 'LAST_QUARTER_MINUS_3',
  LastQuarterMinus4: 'LAST_QUARTER_MINUS_4',
} as const satisfies Record<string, ScheduleReportDataCollectionPeriodType>

// -- SCHEDULED REPORT STATUS FILTER --
export type ScheduleReportStatuses =
  (typeof ScheduleReportStatus)[keyof typeof ScheduleReportStatus]
export const ScheduleReportStatus = {
  Active: 'ACTIVE',
  Paused: 'PAUSED',
} as const

export type ScheduleFormCustomFrequency = {
  repeatEvery?: number
  repeatEveryUnit?: ScheduleReportCustomFrequencyUnitType
  repeatOnDays?: string[] | null
  monthlyOption?: ScheduleReportMonthlyOptionType | null
  endsType?: ScheduleReportCustomFrequencyEndsType
  endsDate?: string | null
  endsAfterOccurrences?: number | null
}

export type ScheduleFormValues = {
  startDate?: string
  time?: string
  timezone?: string
  frequency?: string
  customFrequency?: ScheduleFormCustomFrequency | null
}
